import { useContext } from "react";
import { getLocale } from "../helpers/locale";
import { SSR } from "../helpers/environment";
import { RouteType } from "../builder/mapping/page";
import { useQueryParams } from "../dataAccess/QueryParams";
import { EditorCtx } from "../wysiwyg/EditorCtx";
import {
  getAnalyticsData,
  setAnalyticsDataField,
} from "../dataAccess/storages/analytics";
import { trackEvent } from "../dataAccess/api/analytics";
import { jsonStringifySafe, simpleHash } from "../helpers/formatter";
import { Item } from "../dataAccess/api/item";
import { User } from "../dataAccess/api/user";
import { analyticsDispatcher } from "./analyticsDispatcher";
import { PageStatusContextType } from "./PageStatusCtx";
import type { HTTPClientV3 } from "../dataAccess/HTTPClientV3";

type AnalyticEventType =
  | "item_click"
  | "discount_click"
  | "discount_click_redirection"
  | "quote_request"
  | "query";

type AnalyticEventParams<T extends AnalyticEventType> = T extends "item_click"
  ? { listingId: string }
  : T extends "discount_click"
    ? { discountId: string; withItem: boolean }
    : T extends "discount_click_redirection"
      ? { oldDiscountId: string; newDiscountId: string }
      : T extends "quote_request"
        ? { listingId: string; discountId?: string }
        : T extends "query"
          ? { searchTerm: string }
          : never;

export interface AnalyticEvent {
  type: AnalyticEventType;
  info: AnalyticEventParams<AnalyticEventType>;
}

export type ErrorPageType = "notFound" | "cookiePermissionRequired";

interface AnalyticsTracker {
  trackIfItemClick(
    APIV3: HTTPClientV3,
    args: { item?: Item; user?: User },
  ): void;
  trackIfItemSearch(APIV3: HTTPClientV3, args: { searchTerm?: string }): void;
  trackIfQuoteRequest(APIV3: HTTPClientV3, args: { item?: Item }): void;
  trackIfPageLoad(args: {
    user?: User;
    pageType?: RouteType;
    pageContext?: PageStatusContextType;
  }): void;
  trackIfPageChange(args: {
    pageType?: RouteType;
    pageContext?: PageStatusContextType;
  }): void;
  trackIfPageError(args: { pageType?: ErrorPageType }): void;
  trackIfDealRedemption(): void;
  trackIfConsentConfirm(): void;
  trackIfDealGetApproval(): void;
  trackIfDealResendApproval(): void;
  trackIfConsentLoaded(): void;
}

class AnalyticsTrackerImpl implements AnalyticsTracker {
  private lastEventTracked: AnalyticEvent | null = null;

  private queue: Promise<unknown>[] = [];

  private lastPageType: RouteType | ErrorPageType | null = null;

  private track<T extends AnalyticEventType>(
    APIV3: HTTPClientV3,
    type: T,
    info: AnalyticEventParams<T>,
  ) {
    const newEvent = { type, info };

    if (
      simpleHash(jsonStringifySafe(this.lastEventTracked) || "") !==
      simpleHash(jsonStringifySafe(newEvent) || "")
    ) {
      this.lastEventTracked = newEvent;

      const promiseToWaitForQueue = new Promise((resolve) => {
        if (this.queue.length) {
          Promise.all(this.queue).finally(() => {
            this.createTrackedAnalytic(APIV3, newEvent).finally(() =>
              resolve(true),
            );
          });
        } else {
          this.createTrackedAnalytic(APIV3, newEvent).finally(() =>
            resolve(true),
          );
        }
      });
      this.queue.push(promiseToWaitForQueue);

      setTimeout(() => {
        // To not track same analytic twice within 500ms range.
        // For example if two events with the type "search" and value "test2" are triggered, only 1 is queued
        this.lastEventTracked = null;
        setAnalyticsDataField("pageHasChanged", "false");
      }, 500);
    }
  }

  private checkPageHasChanged() {
    const { pageHasChanged } = getAnalyticsData();
    return pageHasChanged === "true";
  }

  private checkDealWasBlurred() {
    const { lastDealVisitedWasBlurred } = getAnalyticsData();
    const pageHasChanged = this.checkPageHasChanged();
    return lastDealVisitedWasBlurred === "true" && !pageHasChanged;
  }

  private checkDealWasUpgraded(item?: Item) {
    const { lastDealVisited } = getAnalyticsData();
    const dealWasBlurred = this.checkDealWasBlurred();
    const pageHasChanged = this.checkPageHasChanged();
    const dealHasChanged =
      !!(item?.deal?.id && item?.deal?.id !== lastDealVisited) ||
      (dealWasBlurred && !item?.blurredDeal?.id);
    return !pageHasChanged && dealHasChanged;
  }

  private dispatch: typeof analyticsDispatcher.dispatch = (event, ...args) => {
    analyticsDispatcher.dispatch(event, ...args);
  };

  private setLastPageType(
    lastPageType: RouteType | ErrorPageType | null | undefined,
  ) {
    if (lastPageType) {
      this.lastPageType = lastPageType;
    }
  }

  private trackIfDiscountClickOrRedirection(
    APIV3: HTTPClientV3,
    {
      item,
      user,
    }: {
      item?: Item;
      user?: User;
    },
  ) {
    const { lastDealVisited } = getAnalyticsData();
    const pageHasChanged = this.checkPageHasChanged();
    const dealWasUpgraded = this.checkDealWasUpgraded(item);
    const dealWasBlurred = this.checkDealWasBlurred();

    if (item?.deal?.id && (pageHasChanged || dealWasUpgraded)) {
      if (
        dealWasUpgraded &&
        !pageHasChanged &&
        !dealWasBlurred &&
        lastDealVisited
      ) {
        if (user?.id) {
          this.track(APIV3, "discount_click_redirection", {
            newDiscountId: item.deal.id,
            oldDiscountId: lastDealVisited,
          });
        }
      } else {
        this.track(APIV3, "discount_click", {
          discountId: item.deal.id,
          withItem: !dealWasBlurred,
        });
        setAnalyticsDataField("lastDealVisited", item?.deal?.id);
      }
    }
  }

  public trackIfItemClick(
    APIV3: HTTPClientV3,
    { item, user }: { item?: Item; user?: User },
  ) {
    const pageHasChanged = this.checkPageHasChanged();
    const dealWasUpgraded = this.checkDealWasUpgraded(item);

    if (item?.id) {
      if (pageHasChanged || dealWasUpgraded) {
        if (item?.deal?.id) {
          this.trackIfDiscountClickOrRedirection(APIV3, {
            item,
            user,
          });
        } else {
          this.track(APIV3, "item_click", { listingId: item.id });
        }
      }
      if (item?.blurredDeal?.id) {
        setAnalyticsDataField("lastDealVisitedWasBlurred", "true");
      } else if (item?.deal?.id) {
        setAnalyticsDataField("lastDealVisitedWasBlurred", undefined);
      }
    }
  }

  public trackIfQuoteRequest(APIV3: HTTPClientV3, { item }: { item: Item }) {
    if (item?.id) {
      this.track(APIV3, "quote_request", {
        listingId: item.id,
        discountId: item.deal?.id,
      });
    }
  }

  public trackIfPageLoad({
    user,
    pageType,
    pageContext,
  }: {
    user?: User;
    pageType?: RouteType;
    pageContext?: PageStatusContextType;
  }) {
    if (pageType && this.lastPageType !== pageType && pageContext?.firstVisit) {
      this.setLastPageType(pageType);
      const locale = getLocale(window.location.pathname)?.locale || "en";
      this.dispatch("builtfirst:page_load", {
        authenticated: !!user?.id,
        locale,
        pageType,
      });
    }
    pageContext?.setVisited();
  }

  public trackIfPageChange({
    pageType,
    pageContext,
  }: {
    pageType?: RouteType;
    pageContext?: PageStatusContextType;
  }) {
    if (
      pageType &&
      this.lastPageType !== pageType &&
      !pageContext?.firstVisit
    ) {
      this.setLastPageType(pageType);
      this.dispatch("builtfirst:page_change", {
        pageType,
      });
    }
  }

  public trackIfPageError({ pageType }: { pageType?: ErrorPageType }) {
    if (pageType && this.lastPageType !== pageType) {
      this.setLastPageType(pageType);
      this.dispatch("builtfirst:page_error", {
        pageType,
      });
    }
  }

  public trackIfDealRedemption() {
    this.dispatch("builtfirst:deal_redemption");
  }

  public trackIfConsentConfirm() {
    this.dispatch("builtfirst:consent_confirm");
  }

  public trackIfDealGetApproval() {
    this.dispatch("builtfirst:deal_get_approval");
  }

  public trackIfDealResendApproval() {
    this.dispatch("builtfirst:deal_resend_approval");
  }

  public trackIfConsentLoaded() {
    this.dispatch("builtfirst:consent_loaded");
  }

  public trackIfItemSearch(
    APIV3: HTTPClientV3,
    { searchTerm }: { searchTerm?: string },
  ) {
    if (!searchTerm) {
      return;
    }
    if (getAnalyticsData().lastQuery !== searchTerm) {
      this.track(APIV3, "query", { searchTerm });
      this.dispatch("builtfirst:search", { searchTerm });
    }
    setAnalyticsDataField("lastQuery", searchTerm || "");
  }

  private createTrackedAnalytic(APIV3: HTTPClientV3, event: AnalyticEvent) {
    return trackEvent(APIV3, event).json();
  }
}

const analyticsTracker = new AnalyticsTrackerImpl();

export function useAnalyticsTracker() {
  const { isEditor } = useContext(EditorCtx);
  const { screenshot } = useQueryParams();
  const noopAnalyticsTracker: AnalyticsTracker = {
    trackIfItemClick: () => {},
    trackIfItemSearch: () => {},
    trackIfQuoteRequest: () => {},
    trackIfPageLoad: () => {},
    trackIfPageChange: () => {},
    trackIfDealRedemption: () => {},
    trackIfConsentConfirm: () => {},
    trackIfPageError: () => {},
    trackIfDealGetApproval: () => {},
    trackIfDealResendApproval: () => {},
    trackIfConsentLoaded: () => {},
  };

  const noAnalytics = SSR || isEditor || screenshot;
  return {
    AnalyticsTracker: noAnalytics ? noopAnalyticsTracker : analyticsTracker,
  };
}
